/**
 *
 * QuestionFooter
 *
 */

import React, { useMemo, Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { Divider, Tooltip } from 'antd';
import { isEmpty, some, find, get, noop, groupBy } from 'lodash';
import classNames from 'classnames';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { compose } from 'redux';

import Pficon from 'components/Pficon';
import PfAlert from 'components/PfAlert';
import { TYPE_DANGER } from 'components/PfAlert/constants';
import { SHUFFLE_NONE } from 'components/DropdownShuffle/constants';
import { FEATURE_SHUFFLE } from 'data/questionFeatures';
import { NODE_FEATURE_SUPPORT } from 'data/featuresPerQuestion';
import { QUESTION_MATRIX_TYPE_NODES } from 'data/nodeTypes';

import conceptsMessages from 'containers/QuestionnairePage/QuestionGroup/ABTest/Concepts/messages';
import { QuotaConditionTypeEnum } from 'utils/adl-services';
import { makeSelectSurveyStatusName } from 'containers/Survey/selectors';
import { QUOTA_MODE } from 'data/quotaModes';
import { QUOTA_MODE_LABEL } from 'containers/QuestionnairePage/messages';
import TooltipQuestionLabel from './TooltipQuestionLabel';
import QuestionLabel from './QuestionLabel';
import {
  hasOtherAnswer,
  hasNoneOfTheAboveAnswer,
  variableIcon,
  filterOutQuotaFromStatements,
} from './utils';
import messages from './messages';
import {
  isAnswerExclusive,
  otherSelector,
} from '../SingleMultipleQuestion/utils';
import useNavigation from './hooks/useNavigation';
import {
  makeSelectRulesWithQuotas,
  makeSelectNodesWithHiddenAnswerReferences,
} from '../../selectors';
import { makeSelectQuestionQuotasMinimal } from '../../selectorUtils/quotasMinimal';
import { getQuestionQuotaMode } from '../../utils';

const tooltipClasses = 'text-size-secondary display-block padding-vertical-xxs';
const textLabelClasses =
  'background-grey-5 background-grey-6-hover border-radius-level-1 text-size-secondary color-grey-10 padding-vertical-tiny padding-horizontal-xxs display-inline-flex';

const TextLabel = ({ children, className = '' }) => (
  <div className={`${textLabelClasses} ${className}`}>{children}</div>
);

function QuestionFooter({
  surveyId,
  questionRefId,
  answers: tempAnswers,
  rows,
  columns,
  type,
  label,
  shuffle = 0,
  goesTo = [],
  goesToWithErrors = [],
  comesFrom = [],
  ratingSize,
  isUnused = false,
  isNoneOfTheseEnabled = false,
  isVisibleByLogicSetForNoneOfThese = false,
  invalidReferences = [],
  invalidReferencesAnswersLogic = [],
  invalidReferencesVariablesLogic = [],
  hasUnusedCF = false,
  isGroup,
  selectedAttributes = [],
  attributes = [],
  hiddenVariables = [],
  readOnly = false,
  limitMin,
  limitMax,
  limitOutOfBounds,
  rulesWithQuotas = [],
  quotasMinimalMap = {},
  quotasFulfilled = false,
  nodesWithHiddenAnswerReferences = [],
}) {
  const { goToLogic, goToHiddenVariableLogic, goToQuotaLogic } = useNavigation({
    surveyId,
  });
  const isMatrix = QUESTION_MATRIX_TYPE_NODES.includes(type);
  const answers = isMatrix ? rows : tempAnswers;

  const quotaMode = useMemo(
    () => getQuestionQuotaMode({ refId: questionRefId, quotasMinimalMap }),
    [questionRefId, quotasMinimalMap],
  );
  const hasSimpleQuotas = quotaMode === QUOTA_MODE.MAX;
  const hasMinimumResponses = quotaMode === QUOTA_MODE.MIN;

  const isShuffleEnabled =
    (NODE_FEATURE_SUPPORT[type]?.includes(FEATURE_SHUFFLE) &&
      shuffle !== SHUFFLE_NONE) ||
    (isGroup && shuffle !== SHUFFLE_NONE);

  const isLimitAnswersEnabled = useMemo(() => {
    if (limitOutOfBounds) {
      return false;
    }

    return !!(limitMin && limitMax);
  }, [limitMin, limitMax, limitOutOfBounds]);

  const isOtherEnabled = useMemo(
    () => hasOtherAnswer({ questionType: type, answers }),
    [type, answers],
  );
  const isOtherExclusive = useMemo(() => {
    if (!isOtherEnabled) return false;
    const otherAnswer = otherSelector(answers);

    return isAnswerExclusive(otherAnswer);
  }, [answers, isOtherEnabled]);
  const isNoneOfTheAboveEnabled = useMemo(
    () => hasNoneOfTheAboveAnswer({ questionType: type, answers }),
    [type, answers],
  );

  const hasCarryForward = useMemo(
    () =>
      some(answers, 'isCarriedForward') || some(columns, 'isCarriedForward'),
    [answers, columns],
  );

  const enrichedHiddenVariables = hiddenVariables.map((variable) => ({
    ...variable,
    icon: variableIcon(variable.action),
  }));

  const comesFromAdjusted = useMemo(
    () => filterOutQuotaFromStatements(comesFrom),
    [comesFrom],
  );

  const goesToAdjusted = useMemo(
    () => filterOutQuotaFromStatements(goesTo),
    [goesTo],
  );

  const hasAdvancedQuotas = useMemo(() => {
    const res = rulesWithQuotas.filter((rule) => rule.nodeId === questionRefId);
    return res.some((rule) => rule.type === QuotaConditionTypeEnum.Advanced);
  }, [rulesWithQuotas, questionRefId]);

  const goesToWithErrorsAdjusted = useMemo(
    () => filterOutQuotaFromStatements(goesToWithErrors),
    [goesToWithErrors],
  );

  const hasLabels = Boolean(
    isNoneOfTheAboveEnabled ||
    isNoneOfTheseEnabled ||
    isOtherEnabled ||
    isShuffleEnabled ||
    ratingSize ||
    hasCarryForward ||
    quotasFulfilled ||
    selectedAttributes.some((attribute) => attribute.selected),
  );
  const hasHiddenAnswerReference =
    nodesWithHiddenAnswerReferences.includes(questionRefId);
  const hasLogic =
    hasHiddenAnswerReference ||
    !isEmpty(comesFromAdjusted) ||
    !isEmpty(goesToAdjusted) ||
    !isEmpty(goesToWithErrorsAdjusted) ||
    !isEmpty(invalidReferences) ||
    !isEmpty(invalidReferencesAnswersLogic) ||
    !isEmpty(invalidReferencesVariablesLogic) ||
    !isEmpty(enrichedHiddenVariables) ||
    isUnused ||
    hasUnusedCF ||
    hasAdvancedQuotas;

  const { comesFromToEndGroup, comesFromRegular } = useMemo(() => {
    const grouped = groupBy(comesFromAdjusted, 'goesToEndGroup');

    return {
      comesFromToEndGroup: grouped.true,
      comesFromRegular: grouped.false,
    };
  }, [comesFromAdjusted]);

  const hasDisplayableSettings = Boolean(
    hasLabels || hasLogic || hasSimpleQuotas || hasMinimumResponses,
  );

  if (!hasDisplayableSettings) {
    return null;
  }

  const tooltipHoverText = isNoneOfTheAboveEnabled
    ? messages.noneOfAboveTooltip
    : messages.noneOfTheseTooltip;

  const skippedMessage = isGroup
    ? messages.skippedGroup
    : messages.skippedQuestion;
  const skippedTooltipMessage = isGroup
    ? messages.skippedGroupTooltip
    : messages.skippedQuestionTooltip;

  const shouldJustifyContentEnd =
    !hasLogic && !quotasFulfilled && !hasSimpleQuotas && !hasMinimumResponses;

  return (
    <footer
      className={classNames(
        'display-flex w-full',
        'padding-vertical-xxs',
        'padding-left-md',
        {
          'flex-justify--end': shouldJustifyContentEnd,
          'flex-justify-between': !shouldJustifyContentEnd,
          'margin-btm-negative-sm': !isGroup,
        },
      )}
      style={readOnly ? {} : { paddingRight: 26 }}
      data-testid="question-footer"
    >
      <div>
        {hasAdvancedQuotas && (
          <span data-testid="advanced-quotas-indicator">
            <TextLabel className="flex-items-center margin-right-xxs text-size-12 line-height-15">
              <button
                type="button"
                className="position-relative inline-block padding-none cursor-pointer"
                onClick={readOnly ? noop : goToQuotaLogic}
              >
                <Pficon
                  style={{ top: 1 }}
                  type="conditional-quota"
                  className="text-size-12 position-relative margin-horizontal-tiny"
                />
                <FormattedMessage {...messages.advancedQuotas} />
              </button>
            </TextLabel>
          </span>
        )}
        {hasSimpleQuotas && (
          <span data-testid="simple-quotas-indicator">
            <TextLabel className="flex-items-center margin-right-xxs text-size-12 line-height-15">
              <span>
                <Pficon
                  style={{ top: 1 }}
                  type="simple-quota"
                  className="text-size-12 position-relative margin-horizontal-tiny"
                />
                <FormattedMessage {...QUOTA_MODE_LABEL[QUOTA_MODE.MAX]} />
              </span>
            </TextLabel>
          </span>
        )}
        {hasMinimumResponses && (
          <span data-testid="minimum-responses-indicator">
            <TextLabel className="flex-items-center margin-right-xxs text-size-12 line-height-15">
              <span>
                <Pficon
                  style={{ top: 1 }}
                  type="minimum-responses"
                  className="text-size-12 position-relative margin-horizontal-tiny"
                />
                <FormattedMessage {...QUOTA_MODE_LABEL[QUOTA_MODE.MIN]} />
              </span>
            </TextLabel>
          </span>
        )}
        {quotasFulfilled && (
          <span data-testid="quotas-fullfiled-indicator">
            <TextLabel className="flex-items-center margin-right-xxs text-size-12 line-height-15 background-red-3 color-red-7">
              <span>
                <Pficon
                  style={{ top: 1 }}
                  type="conditional-quota"
                  className="text-size-12 position-relative margin-horizontal-tiny"
                />
                <FormattedMessage {...messages.quotasFulfilled} />
              </span>
            </TextLabel>
          </span>
        )}
        {hasLogic && (
          <span className="color-grey-10">
            <Tooltip
              overlayClassName="tooltip-light logic-tooltip"
              placement="bottomLeft"
              title={
                <span className="text-size-secondary">
                  <span className="font-bold">{label}</span>
                  {!isEmpty(comesFromRegular) && (
                    <span className="border-left-grey-5 inline-block padding-vertical-xxs margin-left-xxs padding-left-xxs">
                      <FormattedMessage {...messages.comesFromLabel} />
                      <span className="margin-left-tiny">
                        {comesFromRegular.map((question, cIndex) => (
                          <TooltipQuestionLabel
                            {...question}
                            key={question.refId}
                            itemIndex={cIndex}
                          />
                        ))}
                      </span>
                    </span>
                  )}
                </span>
              }
            >
              <span>
                {!isEmpty(comesFromRegular) && (
                  <TextLabel className="margin-right-xxs text-size-12 line-height-15">
                    <button
                      type="button"
                      className="position-relative inline-block padding-none cursor-pointer"
                      onClick={readOnly ? noop : goToLogic}
                    >
                      <span>{label}</span>
                      <Pficon
                        style={{ top: 1 }}
                        className="color-purple-6 text-size-12 position-relative margin-horizontal-tiny"
                        type="logic-comes-from"
                      />
                      {comesFromRegular.map((question, qIndex) => (
                        <QuestionLabel
                          {...question}
                          itemIndex={qIndex}
                          key={question.refId}
                        />
                      ))}
                    </button>
                  </TextLabel>
                )}
              </span>
            </Tooltip>
            <Tooltip
              overlayClassName="tooltip-light logic-tooltip"
              placement="bottomLeft"
              title={
                <span className="text-size-secondary">
                  <span className="font-bold">{`${label} end`}</span>
                  {!isEmpty(comesFromToEndGroup) && (
                    <span className="border-left-grey-5 inline-block padding-vertical-xxs margin-left-xxs padding-left-xxs">
                      <FormattedMessage {...messages.comesFromLabel} />
                      <span className="margin-left-tiny">
                        {comesFromToEndGroup.map((question, cIndex) => (
                          <TooltipQuestionLabel
                            {...question}
                            key={question.refId}
                            itemIndex={cIndex}
                          />
                        ))}
                      </span>
                    </span>
                  )}
                </span>
              }
            >
              <span>
                {!isEmpty(comesFromToEndGroup) && (
                  <TextLabel className="margin-right-xxs text-size-12 line-height-15">
                    <button
                      type="button"
                      className="position-relative inline-block padding-none cursor-pointer"
                      onClick={readOnly ? noop : goToLogic}
                    >
                      <span>{`${label} end`}</span>
                      <Pficon
                        style={{ top: 1 }}
                        className="color-purple-6 text-size-secondary position-relative margin-horizontal-tiny"
                        type="logic-comes-from"
                      />
                      {comesFromToEndGroup.map((question, qIndex) => (
                        <QuestionLabel
                          {...question}
                          itemIndex={qIndex}
                          key={question.refId}
                        />
                      ))}
                    </button>
                  </TextLabel>
                )}
              </span>
            </Tooltip>
            <Tooltip
              overlayClassName="tooltip-light logic-tooltip"
              placement="bottomLeft"
              title={
                <span className="text-size-secondary">
                  <span className="font-bold">{label}</span>
                  {!isEmpty(goesToAdjusted) && (
                    <span className="border-left-grey-5 inline-block padding-vertical-xxs margin-left-xxs padding-left-xxs">
                      <FormattedMessage {...messages.goesToLabel} />
                      <span className="margin-left-tiny">
                        {goesToAdjusted.map((data, gIndex) => (
                          <TooltipQuestionLabel
                            itemIndex={gIndex}
                            {...data}
                            key={`goes-to-${data.refId}`}
                          />
                        ))}
                      </span>
                    </span>
                  )}
                </span>
              }
            >
              <span>
                {!isEmpty(goesToAdjusted) && (
                  <TextLabel className="margin-right-xxs text-size-12 line-height-15">
                    <button
                      type="button"
                      className="position-relative inline-block padding-none cursor-pointer"
                      onClick={readOnly ? noop : goToLogic}
                    >
                      <span>{label}</span>
                      <Pficon
                        style={{ top: 1 }}
                        className="color-purple-6 text-size-secondary position-relative margin-horizontal-tiny"
                        type="logic-goes-to"
                      />
                      <div className="position-relative inline-block">
                        {goesToAdjusted.map((question, qindex) => (
                          <QuestionLabel
                            {...question}
                            itemIndex={qindex}
                            key={`goes-to-${question.refId}`}
                          />
                        ))}
                      </div>
                    </button>
                  </TextLabel>
                )}
              </span>
            </Tooltip>
            {!isEmpty(goesToWithErrorsAdjusted) && (
              <Tooltip
                overlayClassName="tooltip-light logic-tooltip"
                placement="bottomLeft"
                title={
                  <span className="text-size-secondary">
                    <span className="font-bold">{label}</span>
                    <div className="border-left-grey-5 inline-block padding-vertical-xxs margin-left-xxs padding-left-xxs">
                      <FormattedMessage {...messages.goesToLabel} />
                      <span className="margin-left-tiny">
                        {goesToWithErrorsAdjusted.map((data, gIndex) => (
                          <TooltipQuestionLabel
                            itemIndex={gIndex}
                            {...data}
                            key={`goes-to-with-error-${data.refId}`}
                          />
                        ))}
                      </span>
                      <Divider
                        type="vertical"
                        className="margin-left-xs margin-right-xxs background-grey-7"
                      />
                      <div className="display-inline-block margin-left-tiny">
                        <PfAlert
                          noBackground
                          noPadding
                          centerIcon
                          title={
                            <FormattedMessage {...messages.withLoopLabel} />
                          }
                          type={TYPE_DANGER}
                        />
                      </div>
                    </div>
                  </span>
                }
              >
                <span>
                  <TextLabel className="margin-right-xxs text-size-12 line-height-15 background-red-3 color-red-7">
                    <button
                      type="button"
                      className="position-relative inline-block padding-none cursor-pointer"
                      onClick={readOnly ? noop : goToLogic}
                    >
                      <span>{label}</span>
                      <Pficon
                        style={{ top: 1 }}
                        className="color-red-7 text-size-secondary position-relative margin-horizontal-tiny"
                        type="logic-goes-to"
                      />
                      <div className="position-relative inline-block">
                        {goesToWithErrorsAdjusted.map((question, qindex) => (
                          <QuestionLabel
                            {...question}
                            itemIndex={qindex}
                            key={`goes-to-${question.refId}`}
                          />
                        ))}
                      </div>
                    </button>
                  </TextLabel>
                </span>
              </Tooltip>
            )}
            {isUnused && (
              <Tooltip
                overlayClassName="tooltip-light logic-tooltip"
                placement="bottomLeft"
                title={
                  <span className="text-size-secondary">
                    <span className="font-bold">{label}</span>
                    <div className="border-left-grey-5 inline-block padding-vertical-xxs margin-left-xxs padding-left-xxs">
                      <FormattedMessage {...skippedTooltipMessage} />
                    </div>
                  </span>
                }
              >
                <span>
                  <TextLabel className="margin-right-xxs text-size-12 line-height-15 color-red-7 background-red-1">
                    <FormattedMessage {...skippedMessage} />
                  </TextLabel>
                </span>
              </Tooltip>
            )}
            <Fragment>
              {enrichedHiddenVariables.map((hiddenVariable) => (
                <TextLabel className="margin-right-xxs text-size-12 line-height-15">
                  <button
                    type="button"
                    className="flex flex-align-items--center padding-none cursor-pointer"
                    onClick={readOnly ? noop : goToHiddenVariableLogic}
                  >
                    <Pficon
                      className="margin-right-xxs"
                      type={hiddenVariable.icon}
                    />
                    <span>{hiddenVariable.label}</span>
                  </button>
                </TextLabel>
              ))}
            </Fragment>
            {hasUnusedCF && (
              <Tooltip
                overlayClassName="tooltip-light logic-tooltip"
                placement="bottomLeft"
                title={
                  <span className="text-size-secondary">
                    <span className="font-bold">{label}</span>
                    <div className="border-left-grey-5 inline-block padding-vertical-xxs margin-left-xxs padding-left-xxs">
                      <FormattedMessage
                        {...messages.skippedCarryForwardTooltip}
                      />
                    </div>
                  </span>
                }
              >
                <span>
                  <TextLabel className="margin-right-xxs text-size-12 line-height-15 color-red-7 background-red-1">
                    <FormattedMessage {...messages.skippedCarryForward} />
                  </TextLabel>
                </span>
              </Tooltip>
            )}
            {!isEmpty(invalidReferences) && (
              <Tooltip
                overlayClassName="tooltip-light logic-tooltip"
                placement="bottomLeft"
                title={
                  <span className="text-size-secondary">
                    <span className="font-bold">{label}</span>
                    <div className="border-left-grey-5 inline-block padding-vertical-xxs margin-left-xxs padding-left-xxs">
                      <FormattedMessage {...messages.wrongReferenceTooltip} />
                    </div>
                  </span>
                }
              >
                <span>
                  <TextLabel className="margin-right-xxs text-size-12 line-height-15 color-orange-7 background-orange-1">
                    <FormattedMessage {...messages.wrongReference} />
                  </TextLabel>
                </span>
              </Tooltip>
            )}
            {hasHiddenAnswerReference && (
              <Tooltip
                overlayClassName="tooltip-light logic-tooltip"
                placement="bottomLeft"
                title={
                  <span className="text-size-secondary">
                    <span className="font-bold">{label}</span>
                    <div className="border-left-grey-5 inline-block padding-vertical-xxs margin-left-xxs padding-left-xxs">
                      <FormattedMessage
                        {...messages.hiddenAnswerReferenceTooltip}
                      />
                    </div>
                  </span>
                }
              >
                <span data-testid="hidden-answer-reference-indicator">
                  <TextLabel className="margin-right-xxs text-size-12 line-height-15 color-orange-7 background-orange-1">
                    <FormattedMessage {...messages.wrongReference} />
                  </TextLabel>
                </span>
              </Tooltip>
            )}
            {!isEmpty(invalidReferencesAnswersLogic) && (
              <Tooltip
                overlayClassName="tooltip-light logic-tooltip"
                placement="bottomLeft"
                title={
                  <span className="text-size-secondary">
                    <span className="font-bold">{label}</span>
                    <div className="border-left-grey-5 inline-block padding-vertical-xxs margin-left-xxs padding-left-xxs">
                      <FormattedMessage {...messages.wrongReferenceTooltip} />
                    </div>
                  </span>
                }
              >
                <span>
                  <TextLabel className="margin-right-xxs text-size-12 line-height-15 color-orange-7 background-orange-1">
                    <FormattedMessage
                      {...messages.wrongDisplayLogicReference}
                    />
                  </TextLabel>
                </span>
              </Tooltip>
            )}
            {!isEmpty(invalidReferencesVariablesLogic) && (
              <Tooltip
                overlayClassName="tooltip-light logic-tooltip"
                placement="bottomLeft"
                title={
                  <span className="text-size-secondary">
                    <span className="font-bold">{label}</span>
                    <div className="border-left-grey-5 inline-block padding-vertical-xxs margin-left-xxs padding-left-xxs">
                      <FormattedMessage {...messages.wrongReferenceTooltip} />
                    </div>
                  </span>
                }
              >
                <span>
                  <TextLabel className="margin-right-xxs text-size-12 line-height-15 color-orange-7 background-orange-1">
                    <FormattedMessage
                      {...messages.wrongVariablesLogicReference}
                    />
                  </TextLabel>
                </span>
              </Tooltip>
            )}
          </span>
        )}
      </div>
      <div className="display-flex flex-align-items--center">
        {hasCarryForward && (
          <Tooltip
            overlayClassName="tooltip-light logic-tooltip"
            placement="bottomLeft"
            title={
              <span className={tooltipClasses}>
                <FormattedMessage {...messages.carryForwardTooltip} />
              </span>
            }
          >
            <span className="margin-left-xs margin-right-tiny color-grey-10 line-height-clear">
              <Pficon type="carry-forward-arrow" />
            </span>
          </Tooltip>
        )}

        {selectedAttributes.length > 0 && (
          <div>
            {selectedAttributes.reduce((accumulator, attribute, aIndex) => {
              if (attribute.selected) {
                accumulator.push(
                  <Tooltip
                    overlayClassName="tooltip-light logic-tooltip"
                    placement="bottom"
                    title={
                      <span className={tooltipClasses}>
                        {get(
                          find(attributes, ['refId', attribute.refId]),
                          'name',
                          '',
                        ) || (
                          <FormattedMessage
                            {...conceptsMessages.attributePlaceholder}
                            values={{ index: aIndex + 1 }}
                          />
                        )}
                      </span>
                    }
                    key={attribute.refId}
                  >
                    <span className="margin-left-xs color-grey-10 text-size-10 text-weight-bold">
                      {`A${aIndex + 1}`}
                    </span>
                  </Tooltip>,
                );
              }

              return accumulator;
            }, [])}
          </div>
        )}
        {isShuffleEnabled && (
          <Tooltip
            overlayClassName="tooltip-light logic-tooltip"
            placement="bottom"
            title={
              <span className={tooltipClasses}>
                <FormattedMessage
                  {...(isGroup
                    ? messages.shuffleQuestionsTooltip
                    : messages.shuffleTooltip)}
                />
              </span>
            }
          >
            <span
              className="margin-left-xs margin-right-tiny color-grey-10 line-height-clear"
              data-testid="shuffling-indicator"
            >
              <Pficon type="folder-sort" />
            </span>
          </Tooltip>
        )}

        {isLimitAnswersEnabled && (
          <Tooltip
            overlayClassName="tooltip-light logic-tooltip"
            placement="bottom"
            title={
              <span className={tooltipClasses}>
                <FormattedMessage {...messages.limitAnswersTooltip} />
              </span>
            }
          >
            <span className="margin-left-xs margin-right-tiny text-size-secondary line-height-clear">
              <TextLabel>{`[${limitMin}...${limitMax}]`}</TextLabel>
            </span>
          </Tooltip>
        )}

        {isOtherEnabled && (
          <Tooltip
            overlayClassName="tooltip-light logic-tooltip"
            placement="bottom"
            title={
              <span className={tooltipClasses}>
                <FormattedMessage {...messages.otherTooltip} />
              </span>
            }
          >
            <span className="margin-left-xs margin-right-tiny text-size-secondary line-height-clear">
              <TextLabel className="flex flex-items-center">
                {isOtherExclusive && (
                  <Pficon
                    type="exclusive"
                    className="text-size-small margin-right-xxs"
                  />
                )}

                <FormattedMessage {...messages.otherLabel} />
              </TextLabel>
            </span>
          </Tooltip>
        )}
        {(isNoneOfTheAboveEnabled || isNoneOfTheseEnabled) && (
          <Tooltip
            overlayClassName="tooltip-light logic-tooltip"
            placement="bottom"
            title={
              <span className={tooltipClasses}>
                <FormattedMessage {...tooltipHoverText} />
              </span>
            }
          >
            <span
              className="margin-left-xs margin-right-tiny text-size-secondary"
              data-testid="none-of-these-indicator"
            >
              <TextLabel className="flex-items-center">
                {isVisibleByLogicSetForNoneOfThese && (
                  <Pficon
                    type="answer-logic"
                    className="text-size-primary margin-right-xxs"
                  />
                )}
                <FormattedMessage {...messages.noneOfTheAboveLabel} />
              </TextLabel>
            </span>
          </Tooltip>
        )}
        {ratingSize && (
          <Tooltip
            overlayClassName="tooltip-light logic-tooltip"
            placement="bottom"
            title={
              <span className="margin-left-xs margin-right-tiny text-size-secondary">
                <FormattedMessage {...messages.ratingTooltip} />
              </span>
            }
          >
            <span className="margin-left-xs margin-right-tiny text-size-secondary color-grey-10 line-height-clear">
              <Pficon type="star" />
              <span className="margin-left-xxs">{ratingSize}</span>
            </span>
          </Tooltip>
        )}
        {!readOnly && hasLabels && (
          <Divider
            className="margin-left-xs"
            type="vertical"
            style={{ marginRight: 20 }}
          />
        )}
      </div>
    </footer>
  );
}

const mapStateToProps = createStructuredSelector({
  rulesWithQuotas: makeSelectRulesWithQuotas,
  quotasMinimalMap: makeSelectQuestionQuotasMinimal,
  surveyStatusName: makeSelectSurveyStatusName,
  nodesWithHiddenAnswerReferences: makeSelectNodesWithHiddenAnswerReferences,
});

const withConnect = connect(mapStateToProps);

TextLabel.propTypes = {
  children: PropTypes.element,
  className: PropTypes.string,
};

export default compose(withConnect)(QuestionFooter);
